import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useManagerScope } from "@/components/ManagerLayout";
import { useAuth } from "@/context/AuthContext";
import { Pagination, usePagination } from "@/components/Pagination";
import { ChevronLeft, ChevronRight, BarChart3, Clock, ListChecks, CalendarDays, Coffee } from "lucide-react";

const LEAVE_LABELS = {
  ANNUAL: "Annual", SICK: "Sick", BEREAVEMENT: "Bereavement",
  PARENTAL: "Parental", ALTERNATIVE_HOLIDAYS: "Alt Holidays",
  PUBLIC_HOLIDAY: "Public Hol", UNPAID: "Unpaid", TOIL: "TOIL",
};

function mondayIso(input) {
  const d = input ? new Date(input + "T00:00:00") : new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}
function addDaysIso(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmt(n, d = 2) { return Number(n || 0).toFixed(d); }
function fmtMoney(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(n) {
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

// =============================================================================
// Week picker — drives every report
// =============================================================================
function WeekPicker({ weekStart, setWeekStart, label = "Week", testId = "report-week" }) {
  const end = addDaysIso(weekStart, 6);
  const labelText = (() => {
    const s = new Date(weekStart + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short" });
    const e = new Date(end + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    return `${s} – ${e}`;
  })();
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="text-xs uppercase tracking-widest text-gray-500">{label}</span>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0"
        onClick={() => setWeekStart(addDaysIso(weekStart, -7))} data-testid={`${testId}-prev`}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm font-medium text-[#1A2744] tabular-nums" data-testid={`${testId}-label`}>{labelText}</span>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0"
        onClick={() => setWeekStart(addDaysIso(weekStart, 7))} data-testid={`${testId}-next`}>
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" className="h-8 text-xs"
        onClick={() => setWeekStart(mondayIso())} data-testid={`${testId}-today`}>
        This week
      </Button>
    </div>
  );
}

// =============================================================================
// Labour Cost
// =============================================================================
function LabourCostReport({ weekStart, scopeToken }) {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  const load = useCallback(() => {
    api.get("/manager/reports/labour-cost", { params: { week_start: weekStart } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [weekStart]);
  useEffect(() => { load(); }, [load, scopeToken]);
  const pg = usePagination(data?.rows || [], 10);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="report-labour-cost">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat title="Actual hours" value={fmt(data.totals.actual_hours)} />
        <Stat title="Scheduled hours" value={fmt(data.totals.scheduled_hours)} />
        {isAdmin ? <Stat title="Actual cost" value={fmtMoney(data.totals.actual_cost)} /> : null}
        {isAdmin ? <Stat title="Scheduled cost" value={fmtMoney(data.totals.scheduled_cost)} /> : null}
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Code</TableHead>
                {isAdmin && <TableHead className="text-right">Rate</TableHead>}
                <TableHead className="text-right">Actual hrs</TableHead>
                <TableHead className="text-right">Scheduled hrs</TableHead>
                {isAdmin && <TableHead className="text-right">Actual cost</TableHead>}
                {isAdmin && <TableHead className="text-right">Scheduled cost</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 7 : 4} className="py-8 text-center text-gray-500">No labour data for this week.</TableCell></TableRow>
              )}
              {pg.pageItems.map((r) => (
                <TableRow key={r.user_id} data-testid={`labour-row-${r.user_id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-xs font-mono text-gray-500">{r.employee_code}</TableCell>
                  {isAdmin && <TableCell className="text-right text-sm">{r.hourly_rate != null ? fmtMoney(r.hourly_rate) : "—"}</TableCell>}
                  <TableCell className="text-right">{fmt(r.actual_hours)}</TableCell>
                  <TableCell className="text-right">{fmt(r.scheduled_hours)}</TableCell>
                  {isAdmin && <TableCell className="text-right font-semibold">{fmtMoney(r.actual_cost)}</TableCell>}
                  {isAdmin && <TableCell className="text-right font-semibold">{fmtMoney(r.scheduled_cost)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="labour" />
    </div>
  );
}

// =============================================================================
// Attendance
// =============================================================================
function AttendanceReport({ weekStart, scopeToken }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/manager/reports/attendance", { params: { week_start: weekStart } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [weekStart]);
  useEffect(() => { load(); }, [load, scopeToken]);
  const pg = usePagination(data?.rows || [], 10);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="report-attendance">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat title="Scheduled shifts" value={data.totals.scheduled} />
        <Stat title="Absences" value={data.totals.absences} subtitle={`> ${data.late_threshold_minutes} min late OR no-show`} />
        <Stat title="Attendance rate" value={fmtPct(data.totals.attendance_rate)} />
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Scheduled</TableHead>
                <TableHead className="text-right">Attended</TableHead>
                <TableHead className="text-right">Late (15m+)</TableHead>
                <TableHead className="text-right">No-shows</TableHead>
                <TableHead className="text-right">Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-gray-500">No rostered shifts in this week.</TableCell></TableRow>
              )}
              {pg.pageItems.map((r) => (
                <TableRow key={r.user_id} data-testid={`attendance-row-${r.user_id}`}>
                  <TableCell className="font-medium">{r.name}<div className="text-[10px] font-mono text-gray-500">{r.employee_code}</div></TableCell>
                  <TableCell className="text-right">{r.scheduled_count}</TableCell>
                  <TableCell className="text-right">{r.attended_count}</TableCell>
                  <TableCell className="text-right text-amber-700">{r.late_count || ""}</TableCell>
                  <TableCell className="text-right text-red-700">{r.no_show_count || ""}</TableCell>
                  <TableCell className="text-right font-semibold">
                    <Badge variant="outline" className={
                      r.attendance_rate >= 0.9 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : r.attendance_rate >= 0.7 ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-red-50 text-red-700 border-red-200"
                    }>{fmtPct(r.attendance_rate)}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="attendance" />
    </div>
  );
}

// =============================================================================
// Timesheet status
// =============================================================================
const STATUS_COLORS = {
  ACTIVE: "bg-blue-50 text-blue-700 border-blue-200",
  COMPLETE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  INCOMPLETE: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-[#C9A84C]/15 text-[#7a6420] border-[#C9A84C]/40",
  FLAGGED: "bg-red-50 text-red-700 border-red-200",
  DISPUTED: "bg-purple-50 text-purple-700 border-purple-200",
};

function TimesheetStatusReport({ weekStart, scopeToken }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/manager/reports/timesheet-status", { params: { week_start: weekStart } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [weekStart]);
  useEffect(() => { load(); }, [load, scopeToken]);
  const pg = usePagination(data?.needs_attention || [], 10);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-4" data-testid="report-timesheet-status">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {Object.entries(data.counts).map(([s, c]) => (
          <Card key={s} className={`border ${STATUS_COLORS[s] ? "" : "bg-white"}`}>
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{s}</div>
              <div className="text-2xl font-bold text-[#1A2744] mt-1" data-testid={`status-count-${s}`}>{c}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{total ? `${((c / total) * 100).toFixed(0)}% of week` : "—"}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      {data.needs_attention.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h4 className="font-semibold text-[#1A2744]">Needs attention ({data.needs_attention.length})</h4>
              <span className="text-xs text-gray-500">INCOMPLETE / FLAGGED / DISPUTED</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clock-in</TableHead>
                  <TableHead>Clock-out</TableHead>
                  <TableHead className="text-right">Net hrs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg.pageItems.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.employee_name}<div className="text-[10px] font-mono text-gray-500">{s.employee_code}</div></TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLORS[s.status] || ""}>{s.status}</Badge></TableCell>
                    <TableCell className="text-xs text-gray-500">{s.clock_in_ts ? new Date(s.clock_in_ts).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-xs text-gray-500">{s.clock_out_ts ? new Date(s.clock_out_ts).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-right">{((s.total_minutes || 0) / 60).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="ts-attn" />
    </div>
  );
}

// =============================================================================
// Leave summary
// =============================================================================
function LeaveSummaryReport({ scopeToken }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/manager/reports/leave-summary").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, scopeToken]);
  const pg = usePagination(data?.rows || [], 10);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  // Show only types with non-zero entitlement to keep table readable.
  const visibleTypes = (data.leave_types || []).filter(
    (t) => (data.totals_by_type[t]?.entitled || 0) > 0
  );
  return (
    <div className="space-y-4" data-testid="report-leave-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {visibleTypes.slice(0, 4).map((t) => {
          const tt = data.totals_by_type[t];
          return (
            <Stat key={t} title={LEAVE_LABELS[t] || t} value={`${fmt(tt.remaining, 1)} hrs`} subtitle={`of ${fmt(tt.entitled, 1)} entitled`} />
          );
        })}
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                {visibleTypes.map((t) => (
                  <TableHead key={t} className="text-right">{LEAVE_LABELS[t] || t}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.length === 0 && (
                <TableRow><TableCell colSpan={visibleTypes.length + 1} className="py-8 text-center text-gray-500">No leave balances yet.</TableCell></TableRow>
              )}
              {pg.pageItems.map((r) => (
                <TableRow key={r.user_id} data-testid={`leave-row-${r.user_id}`}>
                  <TableCell className="font-medium">{r.name}<div className="text-[10px] font-mono text-gray-500">{r.employee_code}</div></TableCell>
                  {visibleTypes.map((t) => {
                    const v = r.by_type[t];
                    if (!v) return <TableCell key={t} className="text-right text-gray-300">—</TableCell>;
                    return (
                      <TableCell key={t} className="text-right" data-testid={`leave-cell-${r.user_id}-${t}`}>
                        <span className="text-emerald-700 font-semibold">{fmt(v.remaining_days, 1)}h</span>
                        <span className="text-[10px] text-gray-400 ml-1">/{fmt(v.entitled_days, 1)}</span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="leave-sum" />
    </div>
  );
}

// =============================================================================
// Upcoming roster
// =============================================================================
function UpcomingRosterReport({ weekStart, scopeToken }) {
  const [data, setData] = useState(null);
  // Default to NEXT week if user hasn't navigated.
  const [localWs, setLocalWs] = useState(() => addDaysIso(mondayIso(), 7));
  const targetWs = weekStart || localWs;
  const load = useCallback(() => {
    api.get("/manager/reports/upcoming-roster", { params: { week_start: targetWs } })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, [targetWs]);
  useEffect(() => { load(); }, [load, scopeToken]);
  const pg = usePagination(data?.employees || [], 10);
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  const dayName = (iso) => new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "short", day: "numeric" });
  const allPublished = data.is_published_everywhere;
  return (
    <div className="space-y-4" data-testid="report-upcoming-roster">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <WeekPicker weekStart={targetWs} setWeekStart={setLocalWs} label="Roster week" testId="upcoming-week" />
        <Badge variant="outline" className={allPublished ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"} data-testid="upcoming-published-badge">
          {data.statuses.length === 0 ? "No roster yet" : data.statuses.join(" / ")}
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat title="Shifts scheduled" value={data.totals.shift_count} />
        <Stat title="Total hours" value={fmt(data.totals.hours)} />
        <Stat title="Employees rostered" value={data.employees.length} />
      </div>
      <Card>
        <CardContent className="p-4">
          <h4 className="font-semibold text-[#1A2744] mb-3">Day breakdown</h4>
          <div className="grid grid-cols-7 gap-2">
            {data.days.map((d) => (
              <div key={d.date} className="border border-gray-200 rounded p-2 text-center" data-testid={`upcoming-day-${d.date}`}>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{dayName(d.date)}</div>
                <div className="font-bold text-lg text-[#1A2744]">{d.shift_count}</div>
                <div className="text-[10px] text-gray-400">{fmt(d.hours, 1)}h</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Shifts</TableHead>
                <TableHead className="text-right">Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.length === 0 && (
                <TableRow><TableCell colSpan={3} className="py-8 text-center text-gray-500">Nobody rostered yet.</TableCell></TableRow>
              )}
              {pg.pageItems.map((e) => (
                <TableRow key={e.user_id} data-testid={`upcoming-emp-${e.user_id}`}>
                  <TableCell className="font-medium">{e.name}<div className="text-[10px] font-mono text-gray-500">{e.employee_code}</div></TableCell>
                  <TableCell className="text-right">{e.shift_count}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(e.hours)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total} pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="upcoming" />
    </div>
  );
}

// =============================================================================
// Stat tile
// =============================================================================
function Stat({ title, value, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">{title}</div>
        <div className="text-2xl font-bold text-[#1A2744] mt-1">{value}</div>
        {subtitle && <div className="text-[10px] text-gray-400 mt-0.5">{subtitle}</div>}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Hub page
// =============================================================================
export default function ManagerReports() {
  const [weekStart, setWeekStart] = useState(() => mondayIso());
  const { scopeToken } = useManagerScope();
  const [tab, setTab] = useState("labour");

  return (
    <div className="space-y-6" data-testid="manager-reports-hub">
      <header>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Reports Hub</h1>
        <p className="text-gray-500 mt-1 text-sm">Labour, attendance, timesheets, leave + the week ahead — all site-scoped.</p>
      </header>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="labour" data-testid="tab-labour"><Coffee className="h-3.5 w-3.5 mr-1.5" />Labour cost</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance"><BarChart3 className="h-3.5 w-3.5 mr-1.5" />Attendance</TabsTrigger>
            <TabsTrigger value="timesheets" data-testid="tab-timesheets"><Clock className="h-3.5 w-3.5 mr-1.5" />Timesheet status</TabsTrigger>
            <TabsTrigger value="leave" data-testid="tab-leave"><ListChecks className="h-3.5 w-3.5 mr-1.5" />Leave summary</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming"><CalendarDays className="h-3.5 w-3.5 mr-1.5" />Upcoming roster</TabsTrigger>
          </TabsList>
          <div className="my-4 flex justify-end">
            {tab !== "leave" && tab !== "upcoming" && (
              <WeekPicker weekStart={weekStart} setWeekStart={setWeekStart} />
            )}
          </div>
          <TabsContent value="labour"><LabourCostReport weekStart={weekStart} scopeToken={scopeToken} /></TabsContent>
          <TabsContent value="attendance"><AttendanceReport weekStart={weekStart} scopeToken={scopeToken} /></TabsContent>
          <TabsContent value="timesheets"><TimesheetStatusReport weekStart={weekStart} scopeToken={scopeToken} /></TabsContent>
          <TabsContent value="leave"><LeaveSummaryReport scopeToken={scopeToken} /></TabsContent>
          <TabsContent value="upcoming"><UpcomingRosterReport scopeToken={scopeToken} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
